// Shared review shapes — previously duplicated across `@developerz/policy`
// (`.maintainer.yml` native `review:` block) and `@developerz/review`
// (`.coderabbit.yaml` translation + finding schema), and drifting: the
// path-instructions copy in `@developerz/review` had no `max` while the
// `@developerz/policy` copy capped at 4000 chars. Centralized here so both
// packages stay in lockstep (#31).

import { z } from 'zod';

/** Reviewer verbosity. `chill` = fewer, higher-signal comments (raises the
 * default severity floor). `assertive` = surfaces more, including minor issues. */
export const ReviewProfile = {
  Chill: 'chill',
  Assertive: 'assertive',
} as const;
export const ReviewProfileSchema = z.enum(ReviewProfile);
export type ReviewProfile = z.infer<typeof ReviewProfileSchema>;

/** Review depth. `inline` = a diff-based review from the BYOK inference path
 * (Phase 2, live). `deep` = dispatched to the user's VM for a full
 * checkout/build/test/lint pass (Phase 3, #263) — designed but not yet wired,
 * so `@developerz/policy` `validate.ts` refuses it until the deep path lands. */
export const ReviewDepth = {
  Inline: 'inline',
  Deep: 'deep',
} as const;
export const ReviewDepthSchema = z.enum(ReviewDepth);
export type ReviewDepth = z.infer<typeof ReviewDepthSchema>;

/** Finding severities, most→least severe. */
export const ReviewSeverity = {
  Critical: 'critical',
  Major: 'major',
  Minor: 'minor',
  Nit: 'nit',
} as const;
export const ReviewSeveritySchema = z.enum(ReviewSeverity);
export type ReviewSeverity = z.infer<typeof ReviewSeveritySchema>;

/** A glob + free-text guidance the reviewer applies to matching files. */
export const ReviewPathInstructionSchema = z.object({
  path: z.string().min(1),
  instructions: z.string().min(1).max(4_000),
});
export type ReviewPathInstruction = z.infer<typeof ReviewPathInstructionSchema>;
